/**
 * useEditorStore - Editor Runtime Store for WYSIWYG Page Builder
 * 
 * Single source of truth for editor state:
 * - Selection: selectedId, hoveredId
 * - Mode: select | textEdit | drag | resize
 * - Viewport: zoom, scroll offsets
 * - History: Action-based undo/redo (past, present, future)
 * - Snapping: enabled flag
 * 
 * HISTORY PATTERN:
 * - Only push to history on committed actions (drag end, resize end, text blur, field commit)
 * - Never push during live movement
 * - Undo/Redo should feel instant (no backend save during undo/redo)
 */

import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';

// Editor modes
export const EDITOR_MODES = {
  SELECT: 'select',
  TEXT_EDIT: 'textEdit',
  DRAG: 'drag',
  RESIZE: 'resize',
};

// Interactive element types (others are read-only)
export const INTERACTIVE_TYPES = ['text', 'image', 'button', 'badge'];

// Maximum history entries (prevent memory explosion)
const MAX_HISTORY_SIZE = 50;

const initialState = {
  // Selection
  selectedId: null,        // Currently selected element ID
  selectedBlockId: null,   // Block containing the selected element
  hoveredId: null,         // Currently hovered element ID
  hoveredBlockId: null,    // Block containing the hovered element
  
  // Mode
  mode: EDITOR_MODES.SELECT,
  
  // Drag state
  dragStartPos: null,      // { x, y } of pointer at drag start
  dragOffset: null,        // { dx, dy } current offset from start
  
  // Resize state
  resizeHandle: null,      // 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'
  resizeStartRect: null,   // { x, y, w, h } at resize start
  
  // Viewport
  zoom: 1,
  scrollX: 0,
  scrollY: 0,
  
  // Options
  snappingEnabled: true,
  
  // History - Action-based (correct way)
  // present = current page state (blocks array)
  // past = array of previous states
  // future = array of undone states (for redo)
  past: [],
  present: null,  // Will be set when page data loads
  future: [],
  
  // Flag to indicate undo/redo in progress (skip auto-save)
  isUndoRedo: false,
  
  // Inline text editing
  editingText: null,       // Original text before editing started
  
  // Element position/size overrides (live during drag/resize, committed on end)
  elementOverrides: {},    // { [elementId]: { x, y, w, h } }
};

function editorReducer(state, action) {
  switch (action.type) {
    case 'SELECT_ELEMENT':
      return {
        ...state,
        selectedId: action.elementId,
        selectedBlockId: action.blockId,
        mode: EDITOR_MODES.SELECT,
        editingText: null,
      };
    
    case 'HOVER_ELEMENT':
      return {
        ...state,
        hoveredId: action.elementId,
        hoveredBlockId: action.blockId,
      };
    
    case 'CLEAR_HOVER':
      return {
        ...state,
        hoveredId: null,
        hoveredBlockId: null,
      };
    
    case 'DESELECT':
      return {
        ...state,
        selectedId: null,
        selectedBlockId: null,
        mode: EDITOR_MODES.SELECT,
        editingText: null,
      };
    
    case 'START_TEXT_EDIT':
      return {
        ...state,
        mode: EDITOR_MODES.TEXT_EDIT,
        editingText: action.originalText,
      };
    
    case 'END_TEXT_EDIT':
      return {
        ...state,
        mode: EDITOR_MODES.SELECT,
        editingText: null,
      };
    
    case 'START_DRAG':
      return {
        ...state,
        mode: EDITOR_MODES.DRAG,
        dragStartPos: action.startPos,
        dragOffset: { dx: 0, dy: 0 },
      };
    
    case 'UPDATE_DRAG':
      return {
        ...state,
        dragOffset: action.offset,
      };
    
    case 'END_DRAG':
      return {
        ...state,
        mode: EDITOR_MODES.SELECT,
        dragStartPos: null,
        dragOffset: null,
      };
    
    case 'START_RESIZE':
      return {
        ...state,
        mode: EDITOR_MODES.RESIZE,
        resizeHandle: action.handle,
        resizeStartRect: action.startRect,
      };
    
    case 'UPDATE_RESIZE':
      return {
        ...state,
        elementOverrides: {
          ...state.elementOverrides,
          [state.selectedId]: {
            ...(state.elementOverrides[state.selectedId] || {}),
            ...action.newRect,
          },
        },
      };
    
    case 'END_RESIZE':
      return {
        ...state,
        mode: EDITOR_MODES.SELECT,
        resizeHandle: null,
        resizeStartRect: null,
      };
    
    case 'SET_ELEMENT_OVERRIDE':
      return {
        ...state,
        elementOverrides: {
          ...state.elementOverrides,
          [action.elementId]: {
            ...(state.elementOverrides[action.elementId] || {}),
            ...action.override,
          },
        },
      };
    
    case 'CLEAR_ELEMENT_OVERRIDE':
      const newOverrides = { ...state.elementOverrides };
      delete newOverrides[action.elementId];
      return { ...state, elementOverrides: newOverrides };
    
    case 'SET_ZOOM':
      return { ...state, zoom: action.zoom };
    
    case 'TOGGLE_SNAPPING':
      return { ...state, snappingEnabled: !state.snappingEnabled };
    
    // =========================================================================
    // HISTORY ACTIONS - Action-based pattern
    // =========================================================================
    
    case 'SET_PRESENT':
      // Initialize or update present state (when page data loads)
      return {
        ...state,
        present: action.pageState,
        isUndoRedo: false,
      };
    
    case 'COMMIT':
      // Push current state to history when an action is committed
      // This is called: on drag end, resize end, text edit blur, element add/delete, inspector field commit
      if (!state.present) return state;
      
      const newPast = [...state.past, state.present].slice(-MAX_HISTORY_SIZE);
      return {
        ...state,
        past: newPast,
        present: action.newState,
        future: [], // Clear redo stack on new commit
        isUndoRedo: false,
      };
    
    case 'UNDO':
      // Move back in history
      if (state.past.length === 0) return state;
      
      const previous = state.past[state.past.length - 1];
      const newPastForUndo = state.past.slice(0, -1);
      
      return {
        ...state,
        past: newPastForUndo,
        present: previous,
        future: [state.present, ...state.future].slice(0, MAX_HISTORY_SIZE),
        isUndoRedo: true, // Flag to skip auto-save
      };
    
    case 'REDO':
      // Move forward in history
      if (state.future.length === 0) return state;
      
      const next = state.future[0];
      const newFuture = state.future.slice(1);
      
      return {
        ...state,
        past: [...state.past, state.present].slice(-MAX_HISTORY_SIZE),
        present: next,
        future: newFuture,
        isUndoRedo: true, // Flag to skip auto-save
      };
    
    case 'CLEAR_UNDO_REDO_FLAG':
      return {
        ...state,
        isUndoRedo: false,
      };
    
    default:
      return state;
  }
}

const EditorStoreContext = createContext(null);

export const EditorStoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const saveTimerRef = useRef(null);
  
  // Action creators
  const selectElement = useCallback((elementId, blockId) => {
    dispatch({ type: 'SELECT_ELEMENT', elementId, blockId });
  }, []);
  
  const hoverElement = useCallback((elementId, blockId) => {
    dispatch({ type: 'HOVER_ELEMENT', elementId, blockId });
  }, []);
  
  const clearHover = useCallback(() => {
    dispatch({ type: 'CLEAR_HOVER' });
  }, []);
  
  const deselect = useCallback(() => {
    dispatch({ type: 'DESELECT' });
  }, []);
  
  const startTextEdit = useCallback((originalText) => {
    dispatch({ type: 'START_TEXT_EDIT', originalText });
  }, []);
  
  const endTextEdit = useCallback(() => {
    dispatch({ type: 'END_TEXT_EDIT' });
  }, []);
  
  const startDrag = useCallback((startPos) => {
    dispatch({ type: 'START_DRAG', startPos });
  }, []);
  
  const updateDrag = useCallback((offset, delta) => {
    dispatch({ type: 'UPDATE_DRAG', offset, delta });
  }, []);
  
  const endDrag = useCallback(() => {
    dispatch({ type: 'END_DRAG' });
  }, []);
  
  const startResize = useCallback((handle, startRect) => {
    dispatch({ type: 'START_RESIZE', handle, startRect });
  }, []);
  
  const updateResize = useCallback((newRect) => {
    dispatch({ type: 'UPDATE_RESIZE', newRect });
  }, []);
  
  const endResize = useCallback(() => {
    dispatch({ type: 'END_RESIZE' });
  }, []);
  
  const setElementOverride = useCallback((elementId, override) => {
    dispatch({ type: 'SET_ELEMENT_OVERRIDE', elementId, override });
  }, []);
  
  const clearElementOverride = useCallback((elementId) => {
    dispatch({ type: 'CLEAR_ELEMENT_OVERRIDE', elementId });
  }, []);
  
  // =========================================================================
  // HISTORY ACTIONS
  // =========================================================================
  
  // Initialize or update current page state (call when page data loads)
  const setPresent = useCallback((pageState) => {
    dispatch({ type: 'SET_PRESENT', pageState });
  }, []);
  
  // Commit a new state to history (call on action completion)
  // This pushes current state to past and sets new state as present
  const commit = useCallback((newState) => {
    dispatch({ type: 'COMMIT', newState });
  }, []);
  
  // Undo last action
  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);
  
  // Redo last undone action
  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);
  
  // Clear the undo/redo flag after save completes
  const clearUndoRedoFlag = useCallback(() => {
    dispatch({ type: 'CLEAR_UNDO_REDO_FLAG' });
  }, []);
  
  // Computed: Can undo?
  const canUndo = state.past.length > 0;
  
  // Computed: Can redo?
  const canRedo = state.future.length > 0;
  
  const value = {
    ...state,
    dispatch,
    selectElement,
    hoverElement,
    clearHover,
    deselect,
    startTextEdit,
    endTextEdit,
    startDrag,
    updateDrag,
    endDrag,
    startResize,
    updateResize,
    endResize,
    setElementOverride,
    clearElementOverride,
    // History
    setPresent,
    commit,
    undo,
    redo,
    canUndo,
    canRedo,
    clearUndoRedoFlag,
    saveTimerRef,
  };
  
  return (
    <EditorStoreContext.Provider value={value}>
      {children}
    </EditorStoreContext.Provider>
  );
};

export const useEditorStore = () => {
  const ctx = useContext(EditorStoreContext);
  if (!ctx) {
    throw new Error('useEditorStore must be used within EditorStoreProvider');
  }
  return ctx;
};

export default useEditorStore;
